import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useCallback } from 'react'
import {
  GetDeviceLogsParams,
  GetDeviceMetricsParams,
  deviceApi
} from '@/api/deviceApi'
import { getPageByUrl } from '@/api/vpApi'
import { TOWER_VERSION_FILE, TRACKING_VERSION_FILE } from '@/config/main'
import { Device } from '@/types/device'
import { PaginatedResponse, WithListParams } from '@/types/pagination'
import {
  buildGpuMemoryMetricKeys,
  parseGpuMemory
} from '@/utils/device/parseGpuMemory'
import { objectToQueryString } from '@/utils/url'

export const GPU_MEMORY_REFETCH_INTERVAL = 60_000

export const deviceQueryKeys = {
  all: ['devices'] as const,
  list: (params?: WithListParams) =>
    [...deviceQueryKeys.all, 'list', params] as const,
  infiniteList: () => [...deviceQueryKeys.all, 'infinite-list'] as const,
  details: (deviceId: string) =>
    [...deviceQueryKeys.all, 'details', deviceId] as const,
  detailsList: (deviceIds: string[]) =>
    [...deviceQueryKeys.all, 'details-list', deviceIds] as const,
  edgeClientVersion: () =>
    [...deviceQueryKeys.all, 'edge-client-version'] as const,
  towerVersion: () => [...deviceQueryKeys.all, 'tower-version'] as const,
  trackingVersion: () => [...deviceQueryKeys.all, 'tracking-version'] as const,
  settings: (deviceId: string) =>
    [...deviceQueryKeys.all, 'settings', deviceId] as const,
  metrics: (deviceId: string, params: GetDeviceMetricsParams) =>
    [...deviceQueryKeys.all, 'metrics', deviceId, params] as const,
  logsInfinite: (params?: GetDeviceLogsParams) =>
    [...deviceQueryKeys.all, 'logs-infinite', params] as const,
  latestClientAction: (deviceId: string, logId?: string | null) =>
    [
      ...deviceQueryKeys.all,
      'latest-client-action',
      deviceId,
      ...(logId ? [logId] : [])
    ] as const
}

export const useGetDevicesInfinite = () => {
  return useInfiniteQuery({
    queryKey: deviceQueryKeys.infiniteList(),
    queryFn: ({ pageParam }) =>
      getPageByUrl<PaginatedResponse<Device[]>>(pageParam),
    getNextPageParam: (lastPage) => {
      return lastPage.next ?? undefined
    },
    initialPageParam: `/v1/devices/?${objectToQueryString({
      limit: 20,
      ordering: 'label'
    })}`
  })
}

export const useGetDeviceList = (
  params: WithListParams,
  options?: { keepPreviousData?: boolean; refetchInterval?: number }
) => {
  return useQuery({
    queryKey: deviceQueryKeys.list(params),
    queryFn: () => deviceApi.getDeviceList(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    refetchInterval: options?.refetchInterval
  })
}

export const useInvalidateDeviceList = () => {
  const queryClient = useQueryClient()

  const invalidateDeviceList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...deviceQueryKeys.all, 'list']
    })
  }, [queryClient])

  return {
    invalidateDeviceList
  }
}

export const useGetDeviceDetails = (deviceId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: deviceQueryKeys.details(deviceId),
    queryFn: () => deviceApi.getDeviceDetails(deviceId),
    enabled
  })
}

export const useGetDeviceDetailsById = (deviceIds: string[]) => {
  return useQueries({
    queries: deviceIds.map((deviceId) => ({
      queryKey: deviceQueryKeys.details(deviceId),
      queryFn: () => deviceApi.getDeviceDetails(deviceId)
    })),
    combine: (results) =>
      Object.fromEntries(
        results.map((result, index) => [deviceIds[index], result.data])
      ) as Record<string, Device | undefined>
  })
}

export const useGetSavedVlmGpus = (deviceIds: string[], enabled = true) => {
  return useQueries({
    queries: deviceIds.map((deviceId) => ({
      queryKey: deviceQueryKeys.settings(deviceId),
      queryFn: () => deviceApi.getDeviceSettings(deviceId),
      enabled,
      retry: (failureCount: number, error: unknown) =>
        !(isAxiosError(error) && error.response?.status === 404) &&
        failureCount < 3
    })),
    combine: (results) => ({
      isLoading: results.some((result) => result.isLoading),
      data: Object.fromEntries(
        results.flatMap((result, index) =>
          result.data ? [[deviceIds[index], result.data.gpu_indexes]] : []
        )
      )
    })
  })
}

export const useGetMultipleDeviceDetails = (
  deviceIds: string[],
  enabled?: boolean
) => {
  return useQuery({
    queryKey: deviceQueryKeys.detailsList(deviceIds),
    queryFn: () => {
      return Promise.all(
        deviceIds.map((deviceId) => deviceApi.getDeviceDetails(deviceId))
      )
    },
    enabled
  })
}

export const useClaimDevice = () => {
  return useMutation({
    mutationFn: deviceApi.claimDevice
  })
}

export const useGetEdgeClientVersion = () => {
  return useQuery({
    queryKey: deviceQueryKeys.edgeClientVersion(),
    queryFn: () => deviceApi.getEdgeClientVersion()
  })
}

export const useGetTowerVersion = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: deviceQueryKeys.towerVersion(),
    queryFn: () => deviceApi.getTowerVersion(),
    enabled: Boolean(TOWER_VERSION_FILE),
    retry: false,
    refetchInterval: (query) => {
      if (!options?.refetchInterval || query.state.status !== 'success') {
        return false
      }

      return options.refetchInterval
    }
  })
}

export const useGetTrackingVersion = (options?: {
  refetchInterval?: number
}) => {
  return useQuery({
    queryKey: deviceQueryKeys.trackingVersion(),
    queryFn: () => deviceApi.getTrackingVersion(),
    enabled: Boolean(TRACKING_VERSION_FILE),
    retry: false,
    refetchInterval: (query) => {
      if (!options?.refetchInterval || query.state.status !== 'success') {
        return false
      }

      return options.refetchInterval
    }
  })
}

export const useTriggerTrackingServiceAction = () => {
  return useMutation({
    mutationFn: deviceApi.triggerTrackingServiceAction
  })
}

export const useTriggerEdgeClientAction = () => {
  return useMutation({
    mutationFn: deviceApi.triggerEdgeClientAction
  })
}

export const useGetLatestClientAction = (
  deviceId: string,
  logId: string | null,
  enabled: boolean
) => {
  return useQuery({
    queryKey: deviceQueryKeys.latestClientAction(deviceId, logId),
    queryFn: () =>
      deviceApi.getLatestClientAction(deviceId, logId ?? undefined),
    refetchInterval: (query) => {
      const { data, error } = query.state

      return data?.status === 'success' || data?.status === 'failed' || error
        ? false
        : 3000
    },
    enabled
  })
}

export const useTriggerVlmServiceAction = () => {
  return useMutation({
    mutationFn: deviceApi.triggerVlmServiceAction
  })
}

export const useTriggerTowerServiceAction = () => {
  return useMutation({
    mutationFn: deviceApi.triggerTowerServiceAction
  })
}

export const useGetDeviceSettings = (deviceId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: deviceQueryKeys.settings(deviceId),
    queryFn: () => deviceApi.getDeviceSettings(deviceId),
    enabled,
    // 404 means no settings exist yet, don't retry, the drawer treats it as a create case
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) return false
      return failureCount < 3
    }
  })
}

export const useInvalidateDeviceSettings = (deviceId: string) => {
  const queryClient = useQueryClient()

  const invalidateDeviceSettings = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: deviceQueryKeys.settings(deviceId)
    })
  }, [queryClient, deviceId])

  return { invalidateDeviceSettings }
}

export const useCreateDeviceSettings = () => {
  return useMutation({
    mutationFn: deviceApi.createDeviceSettings
  })
}

export const useUpdateDeviceSettings = () => {
  return useMutation({
    mutationFn: deviceApi.updateDeviceSettings
  })
}

export const usePingDevice = () => {
  return useMutation({ mutationFn: deviceApi.pingDevice })
}

export const useGetDeviceMetrics = (
  deviceId: string,
  params: GetDeviceMetricsParams
) => {
  return useQuery({
    queryKey: deviceQueryKeys.metrics(deviceId, params),
    queryFn: () => deviceApi.getDeviceMetrics(deviceId, params)
  })
}

export const useGetDeviceGpuMemory = (
  deviceId: string,
  gpuIndexes: number[],
  enabled?: boolean
) => {
  const params = { keys: buildGpuMemoryMetricKeys(gpuIndexes), limit: 1 }

  return useQuery({
    queryKey: deviceQueryKeys.metrics(deviceId, params),
    queryFn: () => deviceApi.getDeviceMetrics(deviceId, params),
    select: parseGpuMemory,
    refetchInterval: GPU_MEMORY_REFETCH_INTERVAL,
    enabled
  })
}

export const useGetDeviceLogsInfinite = (
  params: GetDeviceLogsParams = {},
  options?: { refetchInterval?: number }
) => {
  const limit = params.limit ?? 50

  return useInfiniteQuery({
    queryKey: deviceQueryKeys.logsInfinite(params),
    queryFn: ({ pageParam }) =>
      deviceApi.getDeviceLogs({ ...params, offset: pageParam, limit }),
    getNextPageParam: (lastPage, _, lastPageParam) =>
      lastPage.next ? lastPageParam + limit : undefined,
    initialPageParam: 0,
    ...options
  })
}
